export const TENANCY_REQUIRED_CODE = "tenancy/required";
export const TENANCY_FORBIDDEN_CODE = "tenancy/forbidden";

export class TenancyRequiredError extends Error {
  readonly code = TENANCY_REQUIRED_CODE;
  readonly status = 400;

  constructor(message = "Select a tenancy before continuing") {
    super(message);
    this.name = "TenancyRequiredError";
  }
}

type TenancyRecoveryState = {
  required: boolean;
  reason: typeof TENANCY_REQUIRED_CODE | typeof TENANCY_FORBIDDEN_CODE | null;
};

let state: TenancyRecoveryState = { required: false, reason: null };
const listeners = new Set<() => void>();

const updateState = (nextState: TenancyRecoveryState) => {
  if (state.required === nextState.required && state.reason === nextState.reason) {
    return;
  }
  state = nextState;
  listeners.forEach((listener) => listener());
};

export const requestTenancySelection = (
  reason: typeof TENANCY_REQUIRED_CODE | typeof TENANCY_FORBIDDEN_CODE = TENANCY_REQUIRED_CODE,
) => updateState({ required: true, reason });

export const clearTenancySelectionRequest = () => updateState({ required: false, reason: null });

export const subscribeToTenancyRecovery = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getTenancyRecoverySnapshot = (): TenancyRecoveryState => state;

type TenancyRecoveryReason = Exclude<TenancyRecoveryState["reason"], null>;

const normalizeTenancyErrorCode = (code: unknown): TenancyRecoveryReason | null => {
  if (code === TENANCY_REQUIRED_CODE || code === "misc/no-tenancy") {
    return TENANCY_REQUIRED_CODE;
  }
  if (code === TENANCY_FORBIDDEN_CODE || code === "misc/invalid-tenancy") {
    return TENANCY_FORBIDDEN_CODE;
  }
  return null;
};

/**
 * Finds tenancy errors inside the wrapper shapes used by api-def, Axios, and
 * the legacy clients. Transport-level codes such as `request/invalid-status`
 * are deliberately ignored so they cannot hide a structured backend code.
 */
const getTenancyErrorReason = (error: unknown, visited = new Set<object>()): TenancyRecoveryReason | null => {
  if (typeof error === "string") {
    const directReason = normalizeTenancyErrorCode(error);
    if (directReason) {
      return directReason;
    }
    if (error.trim().startsWith("{")) {
      try {
        return getTenancyErrorReason(JSON.parse(error), visited);
      } catch {
        return null;
      }
    }
    return null;
  }
  if (!error || typeof error !== "object" || visited.has(error)) {
    return null;
  }

  visited.add(error);
  const candidate = error as Record<string, unknown>;
  const directReason = normalizeTenancyErrorCode(candidate.code);
  if (directReason) {
    return directReason;
  }

  const response = candidate.response;
  const nestedCandidates = [
    response && typeof response === "object" ? (response as Record<string, unknown>).data : null,
    candidate.data,
    candidate.error,
    candidate.cause,
    response,
  ];
  for (const nestedCandidate of nestedCandidates) {
    const nestedReason = getTenancyErrorReason(nestedCandidate, visited);
    if (nestedReason) {
      return nestedReason;
    }
  }

  return null;
};

export const handleTenancyApiError = (error: unknown): boolean => {
  const reason = getTenancyErrorReason(error);
  if (!reason) {
    return false;
  }
  requestTenancySelection(reason);
  return true;
};

/**
 * Axios response transformer used by the Interview SDK. It observes both
 * successful and rejected response bodies before the SDK can catch an Axios
 * failure, while preserving Axios' usual JSON parsing behaviour.
 */
export const observeTenancyApiResponseData = (data: unknown): unknown => {
  let parsedData = data;
  if (typeof data === "string" && data.trim()) {
    try {
      parsedData = JSON.parse(data);
    } catch {
      // Non-JSON responses such as the rules-engine script stay as strings.
    }
  }
  handleTenancyApiError(parsedData);
  return parsedData;
};

/** Observes a failed fetch response without consuming its body. */
export const handleTenancyHttpResponse = async (response: Response): Promise<boolean> => {
  if (response.ok) {
    return false;
  }

  try {
    return handleTenancyApiError(await response.clone().json());
  } catch {
    return false;
  }
};
